// src/pages/InviteLanding.tsx
// Public landing page for invitation acceptance.
// URL: /invite/:token
// Validates the invitation, lets the recipient set up their account,
// auto-approves them (since they were invited by an admin), and redirects to dashboard.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';

interface Invitation {
  id: string;
  email: string;
  role_intended: 'manager' | 'allocator';
  firm_name_suggested: string | null;
  firm_id: string | null;
  custom_message: string | null;
  status: 'sent' | 'opened' | 'accepted' | 'expired' | 'revoked';
  sent_at: string;
  expires_at: string;
  inviter?: { full_name: string };
}

export default function InviteLanding() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Account form
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firmName, setFirmName] = useState('');
  const [firmType, setFirmType] = useState('hedge_fund');
  const [firmHQ, setFirmHQ] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
  if (!token) {
    setError('Invalid invitation link.');
    setLoading(false);
    return;
  }
  // Sign out any existing session so the invite recipient creates their own account
  supabase.auth.signOut().then(() => {
    loadInvitation();
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [token]);

  const loadInvitation = async () => {
    setLoading(true);
    const { data, error: fetchErr } = await supabase
      .from('invitations')
      .select('*, inviter:profiles!invitations_invited_by_fkey(full_name)')
      .eq('token', token)
      .maybeSingle();

    if (fetchErr || !data) {
      setError('Invitation not found. The link may be invalid or has been revoked.');
      setLoading(false);
      return;
    }

    if (data.status === 'revoked') {
      setError('This invitation has been revoked. Please contact the person who invited you.');
      setLoading(false);
      return;
    }
    if (data.status === 'accepted') {
      setError('This invitation has already been accepted. Please sign in to your account.');
      setLoading(false);
      return;
    }
    if (data.status === 'expired' || new Date(data.expires_at) < new Date()) {
      setError('This invitation has expired. Please contact the person who invited you.');
      setLoading(false);
      return;
    }

    // Mark as opened if not already
    if (data.status === 'sent') {
      await supabase.from('invitations')
        .update({ status: 'opened', opened_at: new Date().toISOString() })
        .eq('id', data.id);
    }

    setInvitation(data as any);
    setFirmName(data.firm_name_suggested || '');
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!invitation) return;
    setSubmitError(null);

    // Validation
    if (!fullName.trim()) { setSubmitError('Please enter your full name.'); return; }
    if (password.length < 8) { setSubmitError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setSubmitError('Passwords do not match.'); return; }
    // Only validate firm fields if creating a new firm (not joining an existing one)
    if (!invitation.firm_id) {
      if (!firmName.trim()) { setSubmitError('Please enter your firm name.'); return; }
      if (!firmHQ.trim()) { setSubmitError('Please enter your firm headquarters location.'); return; }
    }

    setSubmitting(true);

    try {
      // 1. Create auth user
      const { data: authData, error: authErr } = await supabase.auth.signUp({
        email: invitation.email,
        password,
      });

      if (authErr || !authData.user) {
        throw new Error(authErr?.message || 'Failed to create account.');
      }

      const userId = authData.user.id;

      // 2. Create profile (auto-approved since invited)
      const { error: profileErr } = await supabase.from('profiles').insert({
        id: userId,
        email: invitation.email,
        full_name: fullName.trim(),
        role: invitation.role_intended,
        account_status: 'approved',
        submitted_at: new Date().toISOString(),
        reviewed_at: new Date().toISOString(),
      });

      if (invitation.firm_id) {
  // Join existing firm as member
  const { error: memberErr } = await supabase.from('firm_members').insert({
    firm_id: invitation.firm_id,
    user_id: userId,
    role: 'member',
    added_by: userId,  // Self-onboarding via invitation (RLS allows user_id = auth.uid())
  });
  if (memberErr) {
    console.error('Failed to join firm:', memberErr);
    throw new Error(`Failed to join firm: ${memberErr.message}`);
  }
} else {
        // Create new firm
        const { data: newFirm, error: firmErr } = await supabase.from('firms').insert({
          name: firmName.trim(),
          type: firmType,
          headquarters: firmHQ.trim(),
          owner_id: userId,
        }).select().single();

        if (firmErr) throw new Error(`Firm creation failed: ${firmErr.message}`);

        // Add to firm_members too
        if (newFirm) {
          await supabase.from('firm_members').insert({
            firm_id: newFirm.id,
            user_id: userId,
            role: 'owner',
            added_by: userId,
          });
        }
      }

      // 4. Mark invitation as accepted
      await supabase.from('invitations')
        .update({ status: 'accepted', accepted_at: new Date().toISOString() })
        .eq('id', invitation.id);

      // 5. Redirect to dashboard
      navigate('/dashboard');
    } catch (err: any) {
      setSubmitError(err.message);
      setSubmitting(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: '#F5F6F8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  };

  const cardStyle: React.CSSProperties = {
    maxWidth: 520,
    width: '100%',
    background: '#FFFFFF',
    border: '1px solid #D0D5DD',
    borderRadius: 4,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    padding: 40,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: 12,
    borderRadius: 3,
    backgroundColor: '#FFFFFF',
    color: '#0A0A0A',
    border: '1px solid #D0D5DD',
    fontSize: 14,
    marginBottom: 14,
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    color: '#6B7280',
    marginBottom: 6,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: 14, color: '#6B7280', textAlign: 'center' }}>Loading invitation...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
            <svg width="32" height="32" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M 4 14 A 10 10 0 0 1 24 14" stroke="#0A1628" strokeWidth="2.5" strokeLinecap="round"/>
              <path d="M 6 14 A 8 8 0 0 0 22 14" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round"/>
              <circle cx="14" cy="14" r="2.5" fill="#C9A84C"/>
            </svg>
            <span style={{ fontFamily: 'Georgia, serif', fontSize: 22, color: '#0A0A0A' }}>Altveno</span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#0A0A0A', marginBottom: 12 }}>
            Invalid invitation
          </div>
          <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6, marginBottom: 24 }}>
            {error}
          </div>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: '#0A1628',
              color: '#FFFFFF',
              border: 'none',
              padding: '12px 24px',
              borderRadius: 3,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  if (!invitation) return null;

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <svg width="32" height="32" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M 4 14 A 10 10 0 0 1 24 14" stroke="#0A1628" strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M 6 14 A 8 8 0 0 0 22 14" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round"/>
            <circle cx="14" cy="14" r="2.5" fill="#C9A84C"/>
          </svg>
          <span style={{ fontFamily: 'Georgia, serif', fontSize: 22, color: '#0A0A0A' }}>Altveno</span>
        </div>

        {/* Welcome */}
        <div style={{ fontSize: 22, fontWeight: 700, color: '#0A0A0A', marginBottom: 8 }}>
          You're invited to join Altveno
        </div>
        <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 24, lineHeight: 1.5 }}>
          <strong style={{ color: '#0A0A0A' }}>{invitation.inviter?.full_name || 'The Altveno team'}</strong> has invited you to join as a{' '}
          <strong style={{ color: '#C9A84C' }}>
            {invitation.role_intended === 'manager' ? 'Fund Manager' : 'Allocator'}
          </strong>.
        </div>

        {invitation.custom_message && (
          <div style={{
            padding: 16, background: '#F5F6F8',
            borderLeft: '3px solid #C9A84C', borderRadius: 4,
            marginBottom: 24,
          }}>
            <div style={{ fontSize: 11, color: '#6B7280', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
              Personal message
            </div>
            <div style={{ fontSize: 14, color: '#0A0A0A', lineHeight: 1.6 }}>
              {invitation.custom_message}
            </div>
          </div>
        )}

        <div style={{ borderTop: '1px solid #D0D5DD', paddingTop: 24, marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#0A0A0A', marginBottom: 16 }}>
            Set up your account
          </div>

          <div style={labelStyle}>Email</div>
          <input type="email" value={invitation.email} disabled style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }} />

          <div style={labelStyle}>Full name *</div>
          <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
            placeholder="Your full name" style={inputStyle} />

          <div style={labelStyle}>Password * (min 8 characters)</div>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••" style={inputStyle} />

          <div style={labelStyle}>Confirm password *</div>
          <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••" style={inputStyle} />

          {!invitation.firm_id && (
            <>
              <div style={{ borderTop: '1px solid #D0D5DD', paddingTop: 16, marginTop: 8, marginBottom: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#0A0A0A', marginBottom: 16 }}>
                  Your firm
                </div>

                <div style={labelStyle}>Firm name *</div>
                <input type="text" value={firmName} onChange={(e) => setFirmName(e.target.value)}
                  placeholder="e.g. Acme Capital" style={inputStyle} />

                <div style={labelStyle}>Firm type</div>
                <select value={firmType} onChange={(e) => setFirmType(e.target.value)} style={inputStyle}>
                  {invitation.role_intended === 'manager' ? (
                    <>
                      <option value="hedge_fund">Hedge Fund</option>
                      <option value="private_equity">Private Equity</option>
                      <option value="venture_capital">Venture Capital</option>
                      <option value="real_estate">Real Estate</option>
                      <option value="credit">Credit</option>
                      <option value="infrastructure">Infrastructure</option>
                      <option value="multi_strategy">Multi-Strategy</option>
                      <option value="other">Other</option>
                    </>
                  ) : (
                    <>
                      <option value="family_office">Family Office</option>
                      <option value="endowment">Endowment</option>
                      <option value="foundation">Foundation</option>
                      <option value="pension">Pension</option>
                      <option value="hnwi">HNWI</option>
                      <option value="fund_of_funds">Fund of Funds</option>
                      <option value="asset_manager">Asset Manager</option>
                      <option value="other">Other</option>
                    </>
                  )}
                </select>

                <div style={labelStyle}>Headquarters *</div>
                <input type="text" value={firmHQ} onChange={(e) => setFirmHQ(e.target.value)}
                  placeholder="e.g. London, UK" style={inputStyle} />
              </div>
            </>
          )}

          {submitError && (
            <div style={{
              padding: 12, borderRadius: 4, marginBottom: 14,
              background: '#C0222A15',
              color: '#C0222A', fontSize: 13,
            }}>
              {submitError}
            </div>
          )}

          <button
            onClick={handleSubmit} disabled={submitting}
            style={{
              width: '100%',
              padding: 14,
              background: '#0A1628', color: '#FFFFFF',
              border: 'none', borderRadius: 3,
              fontSize: 14, fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
              marginTop: 8,
            }}
          >
            {submitting ? 'Creating your account...' : 'Accept invitation and create account'}
          </button>
        </div>

        <div style={{ marginTop: 16, fontSize: 12, color: '#6B7280', textAlign: 'center' }}>
          By creating an account, you agree to Altveno's <a href="/privacy" style={{ color: '#0D6E8A' }}>Privacy Policy</a> and Terms of Service.
        </div>
      </div>
    </div>
  );
}
